<template>
  <div v-if="isSuspended" class="org-suspended" role="alert">
    <div class="org-suspended__inner">
      <div class="org-suspended__icon" aria-hidden="true">
        <BaseIcon name="exclamation-triangle" size="22" />
      </div>
      <div class="org-suspended__copy">
        <p class="org-suspended__title">{{ $t('common.organization_suspended.title') }}</p>
        <p class="org-suspended__body">
          {{ $t('common.organization_suspended.body', { email: supportEmail }) }}
        </p>
      </div>
      <button type="button" class="org-suspended__cta" @click="contactSupport">
        <BaseIcon name="envelope" size="14" />
        <span>{{ $t('common.organization_suspended.cta') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useUserStore } from '@/store/userStore';
  import { isChatwootEnabled, openChatwootChat } from '@/plugins/chatwoot';

  const SUPPORT_EMAIL = 'support@getfunnelflow.com';

  export default {
    name: 'OrganizationSuspendedBanner',
    components: { BaseIcon },
    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const userStore = useUserStore();

      const isSuspended = computed(
        () => userStore.isAuthenticated && userStore.isCurrentOrganizationSuspended
      );

      const contactSupport = () => {
        if (isChatwootEnabled()) {
          openChatwootChat();
          return;
        }
        const subject = encodeURIComponent($t('common.organization_suspended.mail_subject'));
        window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
      };

      return {
        isSuspended,
        supportEmail: SUPPORT_EMAIL,
        contactSupport,
      };
    },
  };
</script>

<style scoped lang="scss">
  .org-suspended {
    background: var(--red-600);
    color: var(--red-50);
    border-bottom: 1px solid color-mix(in srgb, var(--slate-900) 18%, var(--red-600));

    &__inner {
      display: flex;
      align-items: center;
      gap: 1rem;
      max-width: 1500px;
      margin: 0 auto;
      padding: 0.875rem 1.25rem;

      @include lg-down {
        flex-wrap: wrap;
        padding: 0.875rem 1rem;
        gap: 0.75rem;
      }
    }

    &__icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 0.75rem;
      background: color-mix(in srgb, var(--white) 16%, transparent);

      .icon {
        color: inherit;
      }
    }

    &__copy {
      flex: 1;
      min-width: 0;
    }

    &__title {
      margin: 0;
      font-size: 1.0625rem;
      font-weight: 700;
      line-height: 1.3;
      letter-spacing: -0.01em;
    }

    &__body {
      margin: 0.2rem 0 0;
      font-size: 0.875rem;
      line-height: 1.45;
      max-width: 62ch;
      color: color-mix(in srgb, var(--red-50) 88%, var(--red-600));
    }

    &__cta {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.875rem;
      background: var(--white);
      border: 1px solid transparent;
      color: var(--red-600);
      border-radius: 0.5rem;
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;
      transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease;

      .icon {
        color: inherit;
      }

      @include hover {
        background: var(--red-50);
        color: var(--red-600);
      }

      &:focus-visible {
        outline: 2px solid var(--white);
        outline-offset: 2px;
      }

      @include lg-down {
        width: 100%;
        justify-content: center;
      }
    }
  }
</style>
